/**
 * CampaignDashboardPage — read-only campaign roster for the current tenant.
 *
 * Layout:
 *   1. Header with gradient title + "Refresh" button.
 *   2. KPI strip (Total / Active / Revenue / Conversions) fed by /stats.
 *   3. Filter bar: state pills, vertical dropdown, search box.
 *   4. Table: Name | Vertical | State | Clicks | Conversions | Revenue |
 *             Updated | Actions.
 *   5. Per-row action buttons: "View" (stub link) + "Copy ID".
 *
 * Styling: emerald-600 → sky-600 gradient headline, 6-8 px rounded corners,
 * zinc-200 borders, Inter font (inherited), light theme only. No orange /
 * purple / dark styles, no emojis.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	CAMPAIGN_STATES,
	computeFilteredCount,
	formatInteger,
	formatRevenue,
	formatUpdatedAt,
	isValidCampaignState,
	statePillTheme,
	uniqueVerticals,
	type CampaignRow,
	type CampaignState,
	type ListFilters,
} from "./api";
import {
	useCampaignList,
	useCampaignStats,
	useInvalidateCampaignDashboard,
} from "./hooks";

// ---------------------------------------------------------------------------
// Shared style tokens — emerald→sky only, zinc borders, 6-8 px radius.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_STATE_ON =
	"rounded-[6px] border border-emerald-600 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700";
const BTN_STATE_OFF =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50";

// ---------------------------------------------------------------------------
// Small presentational subcomponents.
// ---------------------------------------------------------------------------

function KpiTile(props: {
	label: string;
	value: string;
	hint?: string;
	testid: string;
}) {
	return (
		<div
			className="rounded-[8px] border border-zinc-200 bg-white p-4 shadow-sm"
			data-testid={props.testid}
		>
			<p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
				{props.label}
			</p>
			<p className="mt-1 text-2xl font-semibold text-zinc-900">
				{props.value}
			</p>
			{props.hint ? (
				<p className="mt-1 text-xs text-zinc-500">{props.hint}</p>
			) : null}
		</div>
	);
}

function StatePill({ state }: { state: CampaignState }) {
	const t = statePillTheme(state);
	return (
		<span
			className={`inline-flex items-center rounded-[6px] px-2 py-0.5 text-[11px] font-semibold ${t.className}`}
			data-testid={`campaign-state-pill-${state}`}
		>
			{t.label}
		</span>
	);
}

function CampaignRowView(props: {
	row: CampaignRow;
	onCopyId: (id: string) => void;
}) {
	const r = props.row;
	return (
		<tr
			className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60"
			data-testid={`campaign-row-${r.id}`}
		>
			<td className="px-3 py-2 text-sm font-medium text-zinc-900">
				<div className="flex flex-col">
					<span>{r.name}</span>
					<span className="font-mono text-[11px] text-zinc-500">
						{r.id}
					</span>
				</div>
			</td>
			<td className="px-3 py-2 text-sm text-zinc-700">{r.vertical}</td>
			<td className="px-3 py-2 text-sm">
				<StatePill state={r.state} />
			</td>
			<td className="px-3 py-2 text-right text-sm text-zinc-700 tabular-nums">
				{formatInteger(r.clicks_count)}
			</td>
			<td className="px-3 py-2 text-right text-sm text-zinc-700 tabular-nums">
				{formatInteger(r.conversions_count)}
			</td>
			<td className="px-3 py-2 text-right text-sm text-zinc-700 tabular-nums">
				{formatRevenue(r.revenue_minor, r.currency)}
			</td>
			<td className="px-3 py-2 text-sm text-zinc-500 tabular-nums">
				{formatUpdatedAt(r.updated_at)}
			</td>
			<td className="px-3 py-2 text-right">
				<div className="inline-flex gap-1">
					<a
						className={BTN_GHOST}
						href={`/campaigns/${encodeURIComponent(r.id)}`}
						data-testid={`campaign-view-${r.id}`}
					>
						View
					</a>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => props.onCopyId(r.id)}
						data-testid={`campaign-copy-${r.id}`}
					>
						Copy ID
					</button>
				</div>
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function CampaignDashboardPage() {
	// UI state — the query key resets when any of these change.
	const [stateFilter, setStateFilter] = useState<CampaignState | null>(null);
	const [vertical, setVertical] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [committedQ, setCommittedQ] = useState<string>("");

	const filters: ListFilters = {
		state: stateFilter ?? undefined,
		vertical: vertical.length > 0 ? vertical : undefined,
		q: committedQ.length > 0 ? committedQ : undefined,
		limit: 25,
	};

	const list = useCampaignList(filters);
	const stats = useCampaignStats();
	const invalidate = useInvalidateCampaignDashboard();

	const items = list.data?.items ?? [];
	const verticals = useMemo(() => uniqueVerticals(items), [items]);
	const filteredCount = items.length;
	// If the tenant types a vertical manually (not from dropdown), make sure
	// the reset UX is still obvious — `computeFilteredCount` gives us a
	// client-side sanity number that always mirrors what's visible.
	const clientFiltered = computeFilteredCount(
		items,
		stateFilter ?? undefined,
		vertical,
	);

	const onSearchSubmit = (e: FormEvent) => {
		e.preventDefault();
		setCommittedQ(q.trim());
	};

	const onCopyId = (id: string) => {
		if (typeof navigator !== "undefined" && navigator.clipboard) {
			navigator.clipboard.writeText(id).catch(() => {
				/* clipboard denials are non-fatal */
			});
		}
	};

	const totalCount = stats.data?.totals.count ?? 0;
	const activeCount = stats.data?.by_state.active?.count ?? 0;
	const conversions = stats.data?.totals.conversions ?? 0;
	const revenueMinor = stats.data?.totals.revenue_minor ?? 0;
	const currency = stats.data?.totals.currency ?? "USD";

	return (
		<main
			data-testid="campaign-dashboard-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10"
		>
			{/* -------------------------- header -------------------------- */}
			<header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-col gap-1">
					<p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
						campaign-dashboard
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Campaign dashboard
					</h1>
					<p className="text-sm text-zinc-600">
						Central roster of every campaign in your tenant. Read-only view.
					</p>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={invalidate}
						disabled={list.isFetching || stats.isFetching}
						data-testid="campaign-dashboard-refresh"
					>
						{list.isFetching ? "Refreshing." : "Refresh"}
					</button>
				</div>
			</header>

			{/* -------------------------- KPI strip -------------------------- */}
			<section
				className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
				aria-label="Campaign KPIs"
				data-testid="campaign-dashboard-kpis"
			>
				<KpiTile
					label="Total campaigns"
					value={formatInteger(totalCount)}
					testid="kpi-total"
				/>
				<KpiTile
					label="Live"
					value={formatInteger(activeCount)}
					hint={`${CAMPAIGN_STATES.length} lifecycle states`}
					testid="kpi-active"
				/>
				<KpiTile
					label="Conversions"
					value={formatInteger(conversions)}
					testid="kpi-conversions"
				/>
				<KpiTile
					label="Revenue"
					value={formatRevenue(revenueMinor, currency)}
					testid="kpi-revenue"
				/>
			</section>

			{/* -------------------------- filters -------------------------- */}
			<section
				className={CARD}
				aria-label="Campaign filters"
				data-testid="campaign-dashboard-filters"
			>
				<div className="flex flex-col gap-4">
					{/* State pills */}
					<div className="flex flex-wrap items-center gap-2">
						<span className="text-xs font-medium text-zinc-500">
							State
						</span>
						<button
							type="button"
							className={
								stateFilter === null ? BTN_STATE_ON : BTN_STATE_OFF
							}
							onClick={() => setStateFilter(null)}
							data-testid="campaign-state-filter-all"
						>
							All
						</button>
						{CAMPAIGN_STATES.map((s) => (
							<button
								type="button"
								key={s}
								className={
									stateFilter === s ? BTN_STATE_ON : BTN_STATE_OFF
								}
								onClick={() => {
									if (isValidCampaignState(s)) setStateFilter(s);
								}}
								data-testid={`campaign-state-filter-${s}`}
							>
								{statePillTheme(s).label}
							</button>
						))}
					</div>

					{/* Vertical + search */}
					<form
						onSubmit={onSearchSubmit}
						className="flex flex-col gap-3 sm:flex-row sm:items-end"
						data-testid="campaign-dashboard-search-form"
					>
						<label className="flex flex-col gap-1 sm:w-56">
							<span className="text-xs font-medium text-zinc-500">
								Vertical
							</span>
							<select
								className={INPUT}
								value={vertical}
								onChange={(e) => setVertical(e.currentTarget.value)}
								data-testid="campaign-vertical-select"
							>
								<option value="">All verticals</option>
								{verticals.map((v) => (
									<option key={v} value={v}>
										{v}
									</option>
								))}
							</select>
						</label>
						<label className="flex flex-1 flex-col gap-1">
							<span className="text-xs font-medium text-zinc-500">
								Search
							</span>
							<input
								type="text"
								placeholder="Name contains."
								className={INPUT}
								value={q}
								onChange={(e) => setQ(e.currentTarget.value)}
								data-testid="campaign-search-input"
							/>
						</label>
						<div className="flex gap-2 sm:pb-[2px]">
							<button
								type="submit"
								className={BTN_PRIMARY}
								data-testid="campaign-search-submit"
							>
								Apply
							</button>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => {
									setStateFilter(null);
									setVertical("");
									setQ("");
									setCommittedQ("");
								}}
								data-testid="campaign-filter-reset"
							>
								Reset
							</button>
						</div>
					</form>
				</div>
			</section>

			{/* -------------------------- table -------------------------- */}
			<section
				className={CARD}
				aria-label="Campaigns"
				data-testid="campaign-dashboard-table-card"
			>
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Campaigns
					</h2>
					<p className="text-xs text-zinc-500">
						Showing {formatInteger(clientFiltered)} of{" "}
						{formatInteger(filteredCount)}
					</p>
				</div>

				{list.isLoading ? (
					<p
						className="py-6 text-center text-sm text-zinc-500"
						data-testid="campaign-dashboard-loading"
					>
						Loading campaigns.
					</p>
				) : list.error ? (
					<p
						className="py-6 text-center text-sm text-red-600"
						role="alert"
						data-testid="campaign-dashboard-error"
					>
						{list.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="py-6 text-center text-sm text-zinc-500"
						data-testid="campaign-dashboard-empty"
					>
						No campaigns match these filters yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-left"
							data-testid="campaign-dashboard-table"
						>
							<thead>
								<tr className="border-b border-zinc-200 bg-zinc-50/70 text-[11px] uppercase tracking-wider text-zinc-500">
									<th className="px-3 py-2 font-semibold">Name</th>
									<th className="px-3 py-2 font-semibold">
										Vertical
									</th>
									<th className="px-3 py-2 font-semibold">State</th>
									<th className="px-3 py-2 text-right font-semibold">
										Clicks
									</th>
									<th className="px-3 py-2 text-right font-semibold">
										Conv.
									</th>
									<th className="px-3 py-2 text-right font-semibold">
										Revenue
									</th>
									<th className="px-3 py-2 font-semibold">Updated</th>
									<th className="px-3 py-2 text-right font-semibold">
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => (
									<CampaignRowView
										key={row.id}
										row={row}
										onCopyId={onCopyId}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}

				{list.data?.next_cursor ? (
					<p className="mt-3 text-right text-xs text-zinc-500">
						More results available — filter to narrow the view.
					</p>
				) : null}
			</section>
		</main>
	);
}

export default CampaignDashboardPage;
